/**
 * `RuntimeState.scheduler` — the pending timed-event queue and its audit
 * trail (architecture.md §3.2, §7.2 step 6).
 *
 * @remarks
 * The queue predates its clock: it shipped at v0 depth with a structural
 * no-op tick, and R9 (D69) made scheduling real — `schedule_event` /
 * `cancel_event` fill it, the tick drains it with fire-time condition
 * re-checks. Due events are selected by `dueTurn`, then `sequenceId`: the
 * sequence counter makes same-turn ordering deterministic, which the replay
 * harness depends on.
 *
 * @packageDocumentation
 */

import { z } from 'zod';

import { eventDefinitionIdSchema } from '../identifiers.js';
import { nonNegativeIntegerSchema } from '../primitives.js';

/** One queued event occurrence: which definition fires, when, and its stable tiebreaker. */
export const pendingScheduledEventSchema = z
	.strictObject({
		dueTurn: nonNegativeIntegerSchema,
		eventDefinitionId: eventDefinitionIdSchema,
		sequenceId: nonNegativeIntegerSchema,
	})
	.readonly();

/** One pending scheduled event — see {@link pendingScheduledEventSchema}. */
export type PendingScheduledEvent = z.infer<typeof pendingScheduledEventSchema>;

/** The pending queue, the monotonic sequence counter, and processed/fired audit ids. */
export const schedulerStateSchema = z
	.strictObject({
		firedEventIds: z.array(eventDefinitionIdSchema).readonly(),
		pendingEvents: z.array(pendingScheduledEventSchema).readonly(),
		processedEventIds: z.array(eventDefinitionIdSchema).readonly(),
		sequenceCounter: nonNegativeIntegerSchema,
	})
	.readonly();

/** The `scheduler` sub-state — see {@link schedulerStateSchema}. */
export type SchedulerState = z.infer<typeof schedulerStateSchema>;
